// src/components/Reports/ReportsView.jsx
import React, { useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Download,
  ExternalLink,
  BookOpen,
  HelpCircle,
  Table,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useReports, useClientConfig, useFeatures } from "../../hooks";
import { getReportDownloadUrl } from "../../services/firebase";

// ============================================
// ICON MAPPING
// ============================================

const iconMap = {
  "file-text": FileText,
  "book-open": BookOpen,
  "help-circle": HelpCircle,
  table: Table,
};

const getIcon = (iconName) => iconMap[iconName] || FileText;

// ============================================
// HELPERS — COLLECT ALL DOWNLOADABLE REPORTS
// ============================================

/**
 * Gathers every downloadable report from the useReports() data.
 * Each entry gets a `folderName` so the zip can organise by avatar.
 * Excludes markdown files (faq.md).
 *
 * Returns: { fileName, storagePath, folderName }[]
 */
const collectAllReports = (avatarGroups, globalReports) => {
  const reports = [];

  avatarGroups.forEach(({ avatar, avatarReports, maps }) => {
    // Avatar-level reports (e.g. methodology)
    Object.values(avatarReports).forEach((report) => {
      if (report.storagePath && report.fileName) {
        reports.push({
          fileName: report.fileName,
          storagePath: report.storagePath,
          folderName: avatar.name,
        });
      }
    });

    // Map-level reports (ranked PDFs, XLSXs, executive summaries)
    maps.forEach(({ mapReports }) => {
      Object.values(mapReports).forEach((report) => {
        if (report.storagePath && report.fileName) {
          reports.push({
            fileName: report.fileName,
            storagePath: report.storagePath,
            folderName: avatar.name,
          });
        }
      });
    });
  });

  // Global reports (excluding markdown / faq)
  globalReports.forEach((report) => {
    if (report.type === "markdown") return;
    if (report.storagePath && report.fileName) {
      reports.push({
        fileName: report.fileName,
        storagePath: report.storagePath,
        folderName: "General",
      });
    }
  });

  return reports;
};

// ============================================
// REPORT ITEM COMPONENT
// ============================================

const ReportItem = ({ report, clientSlug, downloadEnabled = true }) => {
  const [openLoading, setOpenLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);

  const fileName = report.fileName || "";
  const ext = fileName.split(".").pop().toLowerCase();
  const isExcel = ext === "xlsx" || ext === "xls" || ext === "csv";
  const isPdf = ext === "pdf";

  const Icon = getIcon(report.icon);

  const handleOpen = useCallback(async () => {
    if (!report.storagePath) return;
    setOpenLoading(true);
    setError(null);
    try {
      const url = await getReportDownloadUrl(clientSlug, report.storagePath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error getting report URL:", err);
      setError("Failed to open report.");
    } finally {
      setOpenLoading(false);
    }
  }, [clientSlug, report]);

  const handleDownload = useCallback(async () => {
    if (!report.storagePath || !downloadEnabled) return;
    setDownloadLoading(true);
    setError(null);
    try {
      const url = await getReportDownloadUrl(clientSlug, report.storagePath);
      const response = await fetch(url);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = report.fileName || "report";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading report:", err);
      setError("Download failed. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  }, [clientSlug, report, downloadEnabled]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 px-3 lg:px-4 rounded-lg hover:bg-gray-50 transition group">
      {/* File info */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 bg-secondary/10 rounded-lg flex-shrink-0">
          <Icon size={18} className="text-secondary" />
        </div>
        <div className="min-w-0">
          <h4 className="font-medium text-gray-800 text-sm truncate">
            {report.title}
          </h4>
          {report.description && (
            <p className="text-xs text-gray-500 truncate">
              {report.description}
            </p>
          )}
          <span className="text-xs text-gray-400 uppercase">{ext}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-shrink-0 ml-10 sm:ml-4">
        {error && <span className="text-xs text-red-500 mr-1">{error}</span>}

        {isPdf && (
          <>
            <button
              onClick={handleOpen}
              disabled={openLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {openLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ExternalLink size={14} />
              )}
              Open
            </button>
            <button
              onClick={handleDownload}
              disabled={downloadLoading || !downloadEnabled}
              title={!downloadEnabled ? "Downloads disabled" : undefined}
              className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg transition text-sm font-medium ${
                downloadEnabled
                  ? "border-secondary text-secondary hover:bg-secondary/5"
                  : "border-gray-300 text-gray-400 cursor-not-allowed"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {downloadLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Download
            </button>
          </>
        )}

        {isExcel && (
          <button
            onClick={handleDownload}
            disabled={downloadLoading || !downloadEnabled}
            title={!downloadEnabled ? "Downloads disabled" : undefined}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition text-sm font-medium ${
              downloadEnabled
                ? "bg-secondary text-white hover:bg-secondary-dark"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {downloadLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Download
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// COLLAPSIBLE SECTION
// ============================================

const CollapsibleSection = ({
  title,
  subtitle,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 lg:px-6 py-3 lg:py-4 text-left hover:bg-gray-50 transition"
      >
        <div className="min-w-0 mr-2">
          <h3 className="font-semibold text-gray-800 text-sm lg:text-base">
            {title}
          </h3>
          {subtitle && (
            <p className="text-xs lg:text-sm text-gray-500 mt-0.5 truncate">
              {subtitle}
            </p>
          )}
        </div>
        {open ? (
          <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-2 lg:px-4 pb-3 lg:pb-4 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAP REPORTS SUB-SECTION
// ============================================

const MapReportsSection = ({
  map,
  mapReports,
  clientSlug,
  downloadEnabled,
}) => {
  const reports = Object.entries(mapReports);
  if (reports.length === 0) return null;

  const geoLabel =
    map.geographyType === "BG"
      ? "Block Group"
      : map.geographyLabel || map.geographyType;

  return (
    <div className="mt-3">
      <div className="px-3 lg:px-4 py-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {map.stateAbbreviation} — {geoLabel}
        </span>
      </div>
      <div className="space-y-1">
        {reports.map(([key, report]) => (
          <ReportItem
            key={`${map.id}_${key}`}
            report={report}
            clientSlug={clientSlug}
            downloadEnabled={downloadEnabled}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// DOWNLOAD ALL BUTTON
// ============================================

const DownloadAllButton = ({
  clientSlug,
  avatarGroups,
  globalReports,
  clientName,
  downloadEnabled = true,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);

  const allReports = useMemo(
    () => collectAllReports(avatarGroups, globalReports),
    [avatarGroups, globalReports],
  );

  const handleDownloadAll = useCallback(async () => {
    if (allReports.length === 0) return;

    setDownloading(true);
    setError(null);
    setProgress({ current: 0, total: allReports.length });

    try {
      // Dynamic import — JSZip is only loaded when the user clicks Download All
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      let filesAdded = 0;

      // Fetch each report and add to zip
      for (let i = 0; i < allReports.length; i++) {
        const report = allReports[i];
        setProgress({ current: i + 1, total: allReports.length });

        try {
          const url = await getReportDownloadUrl(
            clientSlug,
            report.storagePath,
          );
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const blob = await response.blob();

          // Organise into subfolders by avatar name
          const filePath = `${report.folderName}/${report.fileName}`;
          zip.file(filePath, blob);
          filesAdded++;
        } catch (fileErr) {
          console.error(`Failed to fetch ${report.fileName}:`, fileErr);
          // Continue with remaining files rather than aborting entirely
        }
      }

      // Check that at least one file was added
      if (filesAdded === 0) {
        throw new Error("No files could be downloaded.");
      }

      // Generate zip and trigger download
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const blobUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = blobUrl;

      // Name the zip after the project name for clarity
      const safeName = (clientName || clientSlug || "reports")
        .replace(/[^a-zA-Z0-9_\- ]/g, "")
        .replace(/\s+/g, "_");
      a.download = `${safeName}_reports.zip`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download All failed:", err);
      setError("Failed to generate zip. Please try again.");
    } finally {
      setDownloading(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [allReports, clientSlug, clientName]);

  if (allReports.length === 0) return null;

  return (
    <div className="flex items-center gap-3">
      {error && <span className="text-xs text-red-500">{error}</span>}
      <button
        onClick={handleDownloadAll}
        disabled={downloading || !downloadEnabled}
        title={!downloadEnabled ? "Downloads disabled" : undefined}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${
          downloadEnabled
            ? "bg-secondary text-white hover:bg-secondary-dark"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {downloading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            <span>
              Downloading {progress.current}/{progress.total}…
            </span>
          </>
        ) : (
          <>
            <Download size={16} />
            <span>Download All</span>
          </>
        )}
      </button>
    </div>
  );
};

// ============================================
// MAIN REPORTS VIEW
// ============================================

const ReportsView = () => {
  const { clientSlug } = useParams();
  const { hasReports, avatarGroups, globalReports } = useReports();
  const { clientInfo } = useClientConfig();
  const { downloadEnabled } = useFeatures();

  const filteredGlobalReports = globalReports.filter(
    (r) => r.type !== "markdown",
  );

  const hasVisibleReports =
    filteredGlobalReports.length > 0 ||
    avatarGroups.some(
      ({ avatarReports, maps }) =>
        Object.keys(avatarReports).length > 0 ||
        maps.some(({ mapReports }) => Object.keys(mapReports).length > 0),
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-3 lg:py-4">
          <div className="flex items-center gap-3 lg:gap-4">
            <Link
              to={`/${clientSlug}`}
              className="flex items-center gap-1.5 lg:gap-2 text-gray-300 hover:text-white transition"
            >
              <ArrowLeft size={18} />
              <span className="text-sm hidden sm:inline">Back to Map</span>
            </Link>
            <div className="h-5 lg:h-6 w-px bg-gray-600" />
            <h1 className="text-lg lg:text-xl font-bold">Reports</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-3 lg:px-4 py-6 lg:py-8">
        <div className="bg-white rounded-xl shadow-sm p-4 lg:p-6 mb-4 lg:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-1 lg:mb-2">
                {clientInfo?.projectName || "Project"} Reports
              </h2>
              <p className="text-sm lg:text-base text-gray-600">
                Download analysis reports and documentation for your market
                analysis.
              </p>
            </div>
            {hasVisibleReports && (
              <DownloadAllButton
                clientSlug={clientSlug}
                avatarGroups={avatarGroups}
                globalReports={globalReports}
                clientName={clientInfo?.name}
                downloadEnabled={downloadEnabled}
              />
            )}
          </div>
        </div>

        {hasVisibleReports ? (
          <div className="space-y-3 lg:space-y-4">
            {avatarGroups.map(({ avatar, avatarReports, maps }) => {
              const hasAvatarReports = Object.keys(avatarReports).length > 0;
              const hasMaps = maps.some(
                ({ mapReports }) => Object.keys(mapReports).length > 0,
              );
              if (!hasAvatarReports && !hasMaps) return null;

              return (
                <CollapsibleSection
                  key={avatar.id}
                  title={avatar.name}
                  subtitle={avatar.description}
                >
                  {hasAvatarReports && (
                    <div className="mt-3 space-y-1">
                      {Object.entries(avatarReports).map(([key, report]) => (
                        <ReportItem
                          key={`${avatar.id}_${key}`}
                          report={report}
                          clientSlug={clientSlug}
                          downloadEnabled={downloadEnabled}
                        />
                      ))}
                    </div>
                  )}
                  {maps.map(({ map, mapReports }) => (
                    <MapReportsSection
                      key={map.id}
                      map={map}
                      mapReports={mapReports}
                      clientSlug={clientSlug}
                      downloadEnabled={downloadEnabled}
                    />
                  ))}
                </CollapsibleSection>
              );
            })}

            {filteredGlobalReports.length > 0 && (
              <CollapsibleSection
                title="General"
                subtitle="Reports applicable to the entire project"
              >
                <div className="mt-3 space-y-1">
                  {filteredGlobalReports.map((report) => (
                    <ReportItem
                      key={report.id}
                      report={report}
                      clientSlug={clientSlug}
                      downloadEnabled={downloadEnabled}
                    />
                  ))}
                </div>
              </CollapsibleSection>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No Reports Available
            </h3>
            <p className="text-gray-500">
              Reports for this project are not yet available. Check back later.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReportsView;
